// src/pages/member-resources/MemberResourceCmsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "../../api/axiosClient";
import LoadingComponent from "../../components/common/Loading";
import HTMLParser from 'html-react-parser';

/** Allowed slugs per prefix (mirrors new tabs & dropdowns)
 *  - member_resources: Guidance / Model Documents / Marketing Tools
 *  - training: Webinars (modules + exams are app pages, not CMS)
 *  - partners: EVCC / TrustMark (top-level Trusted Partners)
 *  - benefits: RECC
 *  - evcc: (for member-facing EVCC pages if you reuse this component)
 */
const ALLOWED_BY_PREFIX = {
  member_resources: {
    guidance: "guidance",
    "model-documents": "model-documents",
    "marketing-tools": "marketing-tools",
  },
  training: {
    webinars: "webinars",
  },
  partners: {
    evcc: "evcc",
    trustmark: "trustmark",
  },
  benefits: {
    recc: "recc",
  },
  evcc: {
    "guidance-documents": "guidance-documents",
    "model-documents": "model-documents",
    "marketing-tools": "marketing-tools",
    "trusted-partners": "trusted-partners",
    benefits: "benefits",
  },
};

// ---- helpers: axios fetch + HTML decoder/sanitizer
async function fetchCmsByPrefix(prefix, locale = "") {
  const params = { prefix };
  if (locale) params.locale = locale;

  const res = await api.get("/cms", { params });
  return res.data || {};
}

function decodeEntities(str) {
  if (typeof str !== "string") return "";
  const ta = document.createElement("textarea");
  ta.innerHTML = str;
  return ta.value;
}

function normalizeCmsHtml(val) {
  const raw = typeof val === "string" ? val : "";
  const looksEncoded = /&lt;|&gt;|&amp;|&quot;|&#39;|&#x[0-9A-Fa-f]+;/.test(raw);
  const decoded = looksEncoded ? decodeEntities(raw) : raw;
  return DOMPurify.sanitize(decoded);
}

// Read plain or { value }
function readText(obj, key, fallback = "") {
  const v = obj?.[key];
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "value" in v) return v.value ?? fallback;
  return fallback;
}

/** Generic CMS page
 *  Usage examples:
 *   <MemberResourceCmsPage prefix="member_resources" />
 *   <MemberResourceCmsPage prefix="training" />
 *   <MemberResourceCmsPage prefix="partners" />
 *   <MemberResourceCmsPage prefix="benefits" />
 *   <MemberResourceCmsPage prefix="evcc" />
 */
export default function MemberResourceCmsPage({ prefix = "member_resources" }) {
  const { slug = "" } = useParams();
  const { pathname } = useLocation();

  const SLUGS = ALLOWED_BY_PREFIX[prefix] || {};
  const defaultSlug = Object.keys(SLUGS)[0] || "guidance";
  const validSlug = SLUGS[slug] ? slug : defaultSlug;
  const cmsKey = SLUGS[validSlug];
  const cmsPrefix = `${prefix}.${cmsKey}.`;

  // Compute baseRoot for canonical redirects
  const inCompany = pathname.startsWith("/company/");
  const inMember = pathname.startsWith("/member/");
  const sectionRoot =
    prefix === "member_resources"
      ? "member-resources"
      : prefix === "training"
      ? "training"
      : prefix === "partners"
      ? "trusted-partners"
      : prefix === "benefits"
      ? "benefits"
      : prefix === "evcc"
      ? "evcc"
      : "member-resources";

  const baseRoot = inCompany ? `/company/${sectionRoot}` : inMember ? `/member/${sectionRoot}` : `/company/${sectionRoot}`;
  const needRedirect = slug !== validSlug;

  const [loading, setLoading] = useState(true);
  const [cms, setCms] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchCmsByPrefix(cmsPrefix);
        if (alive) setCms(data || {});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [cmsPrefix]);

    useEffect(() => {
        let t = readText(cms, `${cmsPrefix}body`, "");

        if (!t || !t.length || t.length <= 0)
            return;

        var sections = document.querySelectorAll(".cms-section.collapsible");
        if (sections.length <= 0)
            return;

        for (let jj = 0; jj < sections.length; jj++) {
            let section = sections[jj];

            let button = document.createElement('div');
            button.className = 'minmaxbtn';

            button.addEventListener('click', (e) => {
                //console.log("click", e);
                let parent = e.target.closest('.collapsible');
                if (parent)
                    parent.classList.toggle('closed');
            });

            section.appendChild(button);
        }
    }, [cms])

  const rawTitle = readText(cms, `${cmsPrefix}title`, "");
  const bodyRaw = readText(cms, `${cmsPrefix}body`, "<p>No content yet.</p>");
  const safeBodyHtml = normalizeCmsHtml(bodyRaw);

  // If the body starts with its own <h1>/<h2>, don't render an extra outer title
  const bodyStartsWithHeading = /^\s*<h[12]\b/i.test(safeBodyHtml);
  const showOuterTitle = rawTitle && !bodyStartsWithHeading;
  const humanSection =
    {
      member_resources: "Member Resources",
      training: "Training",
      partners: "Trusted Partners",
      benefits: "RECC Benefits",
      evcc: "EVCC",
    }[prefix] || "Content";

  const fallbackTitle =
    rawTitle ||
    (validSlug.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) || humanSection);

  if (needRedirect) {
    return <Navigate to={`${baseRoot}/${validSlug}`} replace />;
    }

    if (loading)
        return (
            <>
                <LoadingComponent></LoadingComponent>
            </>
        )

    return (
        <div className="cms-page recc">
            <div className="cms-header">
                <div className="cms-title">{ showOuterTitle? rawTitle: fallbackTitle }</div>
            </div>

            <div className="cms-body">

                {safeBodyHtml ?
                    <>
                        {HTMLParser(safeBodyHtml)}
                    </>
                    :
                    <>
                        <div className="cms-section">
                            <div className="cms-greybox">
                                <h3>No content has been added for this section yet.</h3>
                            </div>
                        </div>
                    </>
                }

            </div>
        </div>
    )
}
